import { Col, Flex, Row, Spin, Switch } from 'antd';
import { ErrorListDisplay } from 'components/ErrorDisplay';
import { Text } from 'components/mvp-typography';
import { useClinicAssessmentBalance } from 'lib/hooks/tanstack/use-clinicAssessmentBalance';
import { useSetProductSupportsOnDemandPurchasesMutation } from 'lib/hooks/use-billing-settings-mutations';
import { useQueryErrorNotifier } from 'lib/hooks/use-query-error-notifier';
import { useClinicianContext } from 'lib/hooks/use-resolveCurrentClinicInfo';
import { useErrorList } from 'lib/utils/useErrorList';

export function SettingsSection() {
  const clinicianContext = useClinicianContext();
  const baselineBalance = useClinicAssessmentBalance('baseline');
  const postInjuryBalance = useClinicAssessmentBalance('post-injury');
  const updateProductOnDemandSettingMutation =
    useSetProductSupportsOnDemandPurchasesMutation();
  const errorList = useErrorList();
  useQueryErrorNotifier(
    updateProductOnDemandSettingMutation,
    errorList,
    'Setting PPU purchase availability'
  );

  const handleToggleAllowPostInjury = (checked: boolean) =>
    updateProductOnDemandSettingMutation.mutateAsync({
      value: checked,
      clinicId: clinicianContext.currentClinic,
      type: 'post-injury'
    });
  const handleToggleAllowBaseline = (checked: boolean) =>
    updateProductOnDemandSettingMutation.mutateAsync({
      type: 'baseline',
      value: checked,
      clinicId: clinicianContext.currentClinic
    });

  const isLoading =
    updateProductOnDemandSettingMutation.isPending ||
    postInjuryBalance.isFetching ||
    baselineBalance.isFetching;
  return (
    <Row>
      <Col md={12}>
        <Flex gap={16} vertical>
          <SwitchQuestion
            text="Allow pay-per-use purchases of Post-Injury tests"
            state={
              postInjuryBalance.data?.data.supportsOnDemandPurchases.value ??
              false
            }
            api={handleToggleAllowPostInjury}
          />
          <SwitchQuestion
            text="Allow pay-per-use purchases of Baseline tests"
            state={
              baselineBalance.data?.data.supportsOnDemandPurchases.value ??
              false
            }
            api={handleToggleAllowBaseline}
          />
          <ErrorListDisplay closeable errorList={errorList} />
        </Flex>
      </Col>
      <Col md={12}>
        <Flex align="end" vertical>
          {isLoading && <Spin />}
        </Flex>
      </Col>
    </Row>
  );
}

function SwitchQuestion(props: {
  api: (newSwitchValue: boolean) => Promise<void | any> | void | any;
  state: boolean;
  text: string;
}) {
  return (
    <Flex gap={16}>
      <Text>{props.text}</Text>
      <Switch checked={props.state} onChange={checked => props.api(checked)} />
    </Flex>
  );
}
